import {ReportType} from '../../../types/report-type'
import {reportColumnLabel} from './reportColumnLabels'
import {ENGLISH_MONTHS, formatReportValue, PORTUGUESE_MONTHS} from './reportFormatting'

export type ReportColumn = {
  id: string
  key: string
  label: string
  index: number
  kind: 'text' | 'number' | 'date'
}
export type ReportSort = {columnId: string; direction: 'ascending' | 'descending'} | null
export const MIN_COLUMN_WIDTH = 96
export const MAX_COLUMN_WIDTH = 800
const collator = new Intl.Collator('pt-BR', {numeric: true, sensitivity: 'base'})
const empty = (value: unknown) => value == null || value === '' || value === '—'

// Keep decimal strings exact, including values larger than Number.MAX_SAFE_INTEGER.
function decimal(value: unknown) {
  if (typeof value !== 'number' && typeof value !== 'string') return null
  let text = String(value)
    .trim()
    .replace(/^R\$\s*/, '')
    .replace(/\s/g, '')
  if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.')
  const match = text.match(/^([+-]?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i)
  if (!match) return null
  const exponent = Number(match[4] || 0)
  if (Math.abs(exponent) > 308) return null
  const digits = match[2] + (match[3] || '')
  const point = match[2].length + exponent
  const integer = (point <= 0 ? '0' : digits.slice(0, point).padEnd(point, '0')).replace(
    /^0+(?=\d)/,
    ''
  )
  const fraction = (point < 0 ? '0'.repeat(-point) + digits : digits.slice(point)).replace(
    /0+$/,
    ''
  )
  return {negative: match[1] === '-' && (integer !== '0' || fraction !== ''), integer, fraction}
}
function compareDecimals(
  a: NonNullable<ReturnType<typeof decimal>>,
  b: NonNullable<ReturnType<typeof decimal>>
) {
  if (a.negative !== b.negative) return a.negative ? -1 : 1
  const sign = a.negative ? -1 : 1
  const integer =
    a.integer.length - b.integer.length ||
    (a.integer < b.integer ? -1 : a.integer > b.integer ? 1 : 0)
  if (integer) return integer * sign
  const length = Math.max(a.fraction.length, b.fraction.length)
  const left = a.fraction.padEnd(length, '0'),
    right = b.fraction.padEnd(length, '0')
  return (left < right ? -1 : left > right ? 1 : 0) * sign
}
function dateKey(value: unknown): {time: number; fraction: string} | null {
  if (value instanceof Date)
    return Number.isFinite(value.getTime()) ? {time: value.getTime(), fraction: ''} : null
  let text = String(value)
  const brazilian = text.match(/^(\d{2})\/(\d{2})\/(\d{4})(.*)$/)
  if (brazilian) text = `${brazilian[3]}-${brazilian[2]}-${brazilian[1]}${brazilian[4]}`
  if (/^\d{4}-\d{2}$/.test(text)) text += '-01'
  if (
    /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(text)
  ) {
    let iso = text.replace(' ', 'T')
    if (iso.length === 10) iso += 'T00:00:00Z'
    else if (!/(?:Z|[+-]\d{2}:\d{2})$/.test(iso)) iso += 'Z'
    const time = Date.parse(iso)
    return Number.isFinite(time) ? {time, fraction: text.match(/\.(\d+)/)?.[1] || ''} : null
  }
  const month = text.match(/^([A-Za-zÀ-ÿ]+)[-/](\d{2}|\d{4})$/)
  if (month) {
    let index = ENGLISH_MONTHS.findIndex((name) => name.toLowerCase() === month[1].toLowerCase())
    if (index < 0)
      index = PORTUGUESE_MONTHS.findIndex((name) => name.toLowerCase() === month[1].toLowerCase())
    if (index >= 0)
      return {
        time: Date.UTC(Number(month[2].length === 2 ? '20' + month[2] : month[2]), index, 1),
        fraction: '',
      }
  }
  return null
}

export function reportColumns(
  type: ReportType,
  columns: string[],
  rows: unknown[][]
): ReportColumn[] {
  return columns.map((key, index) => {
    const values = rows.map((row) => row[index]).filter((value) => !empty(value))
    const identifier = /^(?:id|sku|ean|cnpj|cpf|cep|telefone|celular|codigo)/i.test(key)
    const kind = identifier
      ? 'text'
      : /^(?:data|dt_|mes|ano_mes$)/i.test(key)
      ? 'date'
      : values.length && values.every((value) => decimal(value))
      ? 'number'
      : 'text'
    return {id: `${key}:${index}`, key, index, kind, label: reportColumnLabel(type, key)}
  })
}

export function sortReportRows(
  rows: unknown[][],
  columns: ReportColumn[],
  sort: ReportSort
): unknown[][] {
  const column = columns.find((item) => item.id === sort?.columnId)
  if (!column || !sort) return rows
  const sign = sort.direction === 'ascending' ? 1 : -1
  return rows
    .map((row, index) => ({row, index}))
    .sort((a, b) => {
      const left = a.row[column.index],
        right = b.row[column.index]
      if (empty(left) || empty(right))
        return empty(left) === empty(right) ? a.index - b.index : empty(left) ? 1 : -1
      let comparison: number
      if (column.kind === 'number') {
        const x = decimal(left),
          y = decimal(right)
        comparison = x && y ? compareDecimals(x, y) : collator.compare(String(left), String(right))
      } else if (column.kind === 'date') {
        const x = dateKey(left),
          y = dateKey(right)
        if (!x || !y)
          return !x === !y
            ? collator.compare(String(left), String(right)) * sign || a.index - b.index
            : x
            ? -1
            : 1
        comparison =
          x.time - y.time || collator.compare(x.fraction.padEnd(9, '0'), y.fraction.padEnd(9, '0'))
      } else comparison = collator.compare(String(left), String(right))
      return comparison * sign || a.index - b.index
    })
    .map((item) => item.row)
}

export function clampColumnWidth(width: number): number {
  return Math.max(MIN_COLUMN_WIDTH, Math.min(MAX_COLUMN_WIDTH, Math.round(width)))
}
export function initialColumnWidth(column: ReportColumn): number {
  const content = /produto|razao|nome|descricao|politica|distribuidor|industria/i.test(column.key)
    ? 240
    : column.kind === 'date'
    ? 170
    : 150
  return clampColumnWidth(Math.max(content, Math.min(column.label.length * 7 + 56, 320)))
}
export function fitColumnWidth(type: ReportType, column: ReportColumn, rows: unknown[][]): number {
  const length = Math.max(
    column.label.length + 5,
    ...rows
      .slice(0, 100)
      .map((row) => formatReportValue(type, column.key, row[column.index]).length)
  )
  return clampColumnWidth(Math.min(length * 7 + 32, 600))
}
